import axios from 'axios';
import { api } from '../DefaultService';
import type {
  IAsyncReportCreateRequest,
  IAsyncReportCreateResponse,
  IAsyncReportProgressResponse,
  IReportProps,
  IStudentAttendanceReportProps,
} from './types';

const ENDPOINT = 'v1/report';
const ASYNC_ENDPOINT = 'v1/report/async';

type ReportRequestOptions = {
  signal?: AbortSignal;
};

const buildQueryString = (
  params: Record<string, string | boolean | string[] | undefined>,
): string => {
  const parts: string[] = [];

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }

    if (Array.isArray(value)) {
      value
        .filter((item) => item !== '')
        .forEach((item) => {
          parts.push(`${key}=${encodeURIComponent(item)}`);
        });
      return;
    }

    if (value !== '') {
      parts.push(`${key}=${encodeURIComponent(String(value))}`);
    }
  });

  return parts.length ? `?${parts.join('&')}` : '';
};

const buildIncludeImagesQuery = (includeImages?: boolean): string => {
  return buildQueryString({ includeImages });
};

type ReportErrorPayload = {
  detail?: string;
  errors?: Record<string, string[]>;
  message?: string;
  title?: string;
  traceId?: string;
};

const parseReportErrorPayload = (
  value: unknown,
): { message?: string; traceId?: string } => {
  if (!value) {
    return {};
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return {};
    }

    try {
      return parseReportErrorPayload(JSON.parse(trimmedValue));
    } catch {
      return { message: trimmedValue };
    }
  }

  if (typeof value !== 'object') {
    return {};
  }

  const payload = value as ReportErrorPayload;
  let validationMessage: string | undefined;

  if (payload.errors) {
    for (const messages of Object.values(payload.errors)) {
      const firstMessage = messages.find((message) => message.trim().length > 0);
      if (firstMessage) {
        validationMessage = firstMessage;
        break;
      }
    }
  }

  return {
    message:
      payload.message ?? payload.title ?? payload.detail ?? validationMessage,
    traceId: payload.traceId,
  };
};

const mapReportErrorMessage = (
  status: number | undefined,
  extractedMessage: string | undefined,
  traceId: string | undefined,
  fallbackMessage: string,
): string => {
  const appendTraceIdIfMissing = (message: string): string => {
    if (!traceId || /traceid\s*:/i.test(message)) {
      return message;
    }

    return `${message} TraceId: ${traceId}.`;
  };

  if (status === 400) {
    return extractedMessage || 'Revise os filtros informados e tente novamente.';
  }

  if (status === 404) {
    return extractedMessage || 'Nenhum registro encontrado para o período.';
  }

  if (status === 500) {
    if (extractedMessage) {
      return appendTraceIdIfMissing(extractedMessage);
    }

    return traceId
      ? `Erro interno ao gerar o relatório. TraceId: ${traceId}. Abra um ticket para o suporte.`
      : 'Erro interno ao gerar o relatório. Abra um ticket para o suporte com o TraceId.';
  }

  if (extractedMessage) {
    return extractedMessage;
  }

  return traceId ? `${fallbackMessage} TraceId: ${traceId}.` : fallbackMessage;
};

const resolveReportErrorMessage = async (
  error: unknown,
  fallbackMessage: string,
): Promise<string> => {
  if (axios.isCancel(error)) {
    return 'Geração cancelada pelo usuário.';
  }

  if (!axios.isAxiosError(error)) {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    return fallbackMessage;
  }

  const status = error.response?.status;

  if (error.code === 'ERR_CANCELED') {
    return 'Geração cancelada pelo usuário.';
  }

  const responseData = error.response?.data;

  if (responseData instanceof Blob) {
    const errorText = await responseData.text();
    const { message, traceId } = parseReportErrorPayload(errorText);
    return mapReportErrorMessage(status, message, traceId, fallbackMessage);
  }

  const { message, traceId } = parseReportErrorPayload(responseData);
  return mapReportErrorMessage(status, message, traceId, fallbackMessage);
};

const requestReportBlobPost = async (
  path: string,
  payload: unknown,
  fallbackMessage: string,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  try {
    const response = await api.post(path, payload, {
      responseType: 'blob',
      timeout: 0,
      signal: options?.signal,
    });

    return response.data;
  } catch (error: unknown) {
    throw new Error(await resolveReportErrorMessage(error, fallbackMessage));
  }
};

const requestReportBlob = async (
  path: string,
  fallbackMessage: string,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  try {
    const response = await api.get(path, {
      responseType: 'blob',
      timeout: 0,
      signal: options?.signal,
    });
    return response.data;
  } catch (error: unknown) {
    throw new Error(await resolveReportErrorMessage(error, fallbackMessage));
  }
};

export const createAsyncReport = async (
  payload: IAsyncReportCreateRequest,
  options?: ReportRequestOptions,
): Promise<IAsyncReportCreateResponse> => {
  try {
    const response = await api.post(`${ASYNC_ENDPOINT}/create`, payload, {
      timeout: 0,
      signal: options?.signal,
    });

    return response.data as IAsyncReportCreateResponse;
  } catch (error: unknown) {
    throw new Error(
      await resolveReportErrorMessage(error, 'Erro ao enfileirar relatório!'),
    );
  }
};

export const getAsyncReportProgress = async (
  jobId: number,
  options?: ReportRequestOptions,
): Promise<IAsyncReportProgressResponse> => {
  try {
    const response = await api.get(`api/v1/jobs/${jobId}`, {
      timeout: 0,
      signal: options?.signal,
    });

    return response.data as IAsyncReportProgressResponse;
  } catch (error: unknown) {
    throw new Error(
      await resolveReportErrorMessage(error, 'Erro ao consultar progresso!'),
    );
  }
};

export const downloadAsyncReportResult = async (
  jobId: number,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  try {
    const response = await api.get(`api/v1/jobs/${jobId}/download`, {
      responseType: 'blob',
      timeout: 0,
      signal: options?.signal,
    });

    return response.data;
  } catch (error: unknown) {
    throw new Error(
      await resolveReportErrorMessage(error, 'Erro ao baixar relatório!'),
    );
  }
};

export const cancelAsyncReport = async (
  jobId: number,
  options?: ReportRequestOptions,
): Promise<void> => {
  try {
    await api.delete(`api/v1/jobs/${jobId}`, {
      timeout: 0,
      signal: options?.signal,
      _suppressError: true,
    });
  } catch (error: unknown) {
    throw new Error(
      await resolveReportErrorMessage(error, 'Erro ao cancelar relatório!'),
    );
  }
};

export const generateStudentReport = async (
  alunoId: number,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/student/${alunoId}/`,
    'Erro ao gerar o relatório do aluno!',
    options,
  );
};

export const generateFacesReport = async (
  data: IReportProps,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  if (data.AllEvents) {
    return await requestReportBlob(
      `${ENDPOINT}/faces/all/${data.ExportType}${buildQueryString({ includeImages: data.IncludeImages, discriminator: data.Discriminators })}`,
      'Erro ao gerar o relatório de faces!',
    );
  }

  return await requestReportBlob(
    `${ENDPOINT}/faces/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildQueryString({ includeImages: data.IncludeImages, discriminator: data.Discriminators })}`,
    'Erro ao gerar o relatório de faces!',
    options,
  );
};
export const generateStudentEventsReport = async (
  data: IReportProps,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/student/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildIncludeImagesQuery(data.IncludeImages)}`,
    'Erro ao gerar o relatório de cadastro de alunos!',
    options,
  );
};

export const generateExitEventsReport = async (
  data: IReportProps,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/exit/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildQueryString({ includeImages: data.IncludeImages, includePersonType: data.IncludePersonType, entityType: data.ExitEntityTypes })}`,
    'Erro ao gerar o relatório de saídas!',
    options,
  );
};

export const generateStudentsWithoutEventsReport = async (
  data: IReportProps,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  const schoolClassIds = data.SchoolClassIds?.map((id) => String(id));

  return await requestReportBlob(
    `${ENDPOINT}/student/noevent/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildQueryString({ includeImages: data.IncludeImages, includeSchoolClass: data.IncludeSchoolClass, schoolClassIds })}`,
    'Erro ao gerar o relatório de alunos sem eventos!',
    options,
  );
};

export const generateEventsReport = async (
  data: IReportProps,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  const alunoIds = data.AlunoIds?.map((id) => String(id));
  const responsibleIds = data.ResponsibleIds?.map((id) => String(id));
  const generalIds = data.GeneralIds?.map((id) => String(id));
  const schoolClassIds = data.SchoolClassIds?.map((id) => String(id));

  return await requestReportBlob(
    `${ENDPOINT}/events/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildQueryString({ includeImages: data.IncludeImages, includeDevice: data.IncludeDevice, includeSchoolClass: data.IncludeSchoolClass, entityType: data.EventEntityTypes, alunoIds, responsibleIds, generalIds, schoolClassIds })}`,
    'Erro ao gerar o relatório de eventos!',
    options,
  );
};

export const generateRecognitionUpdatesAuditReport = async (
  data: IReportProps,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/recognition/updates/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildQueryString({ includeImages: data.IncludeImages, includeCreatedAt: data.IncludeCreatedAt, includeResponsibleUser: data.IncludeResponsibleUser, discriminator: data.Discriminators })}`,
    'Erro ao gerar o relatório de auditoria de atualizações!',
    options,
  );
};
export const generateDeviceReport = async (
  data: IReportProps,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  if (data.AllEvents) {
    return await requestReportBlob(
      `${ENDPOINT}/device/all/${data.ExportType}${buildQueryString({ includeIp: data.IncludeIp })}`,
      'Erro ao gerar o relatório de dispositivos!',
      options,
    );
  }

  return await requestReportBlob(
    `${ENDPOINT}/device/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildQueryString({ includeIp: data.IncludeIp })}`,
    'Erro ao gerar o relatório de dispositivos!',
    options,
  );
};

export const generateStudentEntriesLast30DaysReport = async (
  id: number,
  exportType: string,
  includeImages?: boolean,
  includeDevice?: boolean,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/student/last30/${id}/${exportType}${buildQueryString({ includeImages, includeDevice })}`,
    'Erro ao gerar o relatório de entradas dos últimos 30 dias!',
    options,
  );
};

export const generateStudentsByClassReport = async (
  schoolClassIds: number[],
  exportType: string,
  includeImages?: boolean,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  const query = schoolClassIds
    .map((id) => `schoolClassIds=${id}`)
    .join('&');

  const includeImagesQuery =
    typeof includeImages === 'boolean' ? `&includeImages=${includeImages}` : '';

  return await requestReportBlob(
    `${ENDPOINT}/student/class/${exportType}?${query}${includeImagesQuery}`,
    'Erro ao gerar o relatório de alunos por turma!',
    options,
  );
};

const normalizeTimeForApi = (value: string): string => {
  const normalized = value.trim();
  return normalized.length === 5 ? `${normalized}:00` : normalized;
};

export const generateStudentAttendanceReport = async (
  data: IStudentAttendanceReportProps,
  options?: ReportRequestOptions,
): Promise<Blob> => {
  const payload = {
    startDate: data.StartDate,
    endDate: data.EndDate,
    entryWindowStart: normalizeTimeForApi(data.EntryWindowStart),
    entryWindowEnd: normalizeTimeForApi(data.EntryWindowEnd),
    toleranceBeforeMinutes: data.ToleranceBeforeMinutes,
    toleranceAfterMinutes: data.ToleranceAfterMinutes,
    minimumExitTime: normalizeTimeForApi(data.MinimumExitTime),
    alunoIds: data.AlunoIds ?? [],
    schoolClassIds: data.SchoolClassIds ?? [],
    includeImages: data.IncludeImages,
    onlyEntry: data.OnlyEntry,
  };

  return await requestReportBlobPost(
    `${ENDPOINT}/student/attendance/${data.ExportType}`,
    payload,
    'Erro ao gerar o relatório de presença de alunos!',
    options,
  );
};
